import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import MathsUtils from '@/utils/mathsUtils'
import Config from '@glass-designer/config'
import Utils from '@/utils/utils'
import BaseObject from '../objects/baseObject'
import GlassShape from './glassShape'
import RectObject from '../objects/rectObject'
import LabelObject from '../objects/labelObject'

import GlassDesignerTypes from '@glass-designer/types'
import { InventoryStockTypes, UOMTypes } from '@/store/types'


/*
 * AssemblyGroup
 * Contains a list of GlassShape objects that represents a single assembly
 */
export default class AssemblyGroup extends BaseObject {
  constructor(editor, itemData = {}) {
    super(editor)
    this.objectClassName = 'AssemblyGroup'

    // Assembly link info
    this.assemblyLinkUUID = uuidv4();

    this.outlineGeometry = null
    this.outlineObj = null
    this.rectangleObjList = []
    this.labelRectObj = null
    this.labelRectLabelObj = null
    this.sideviewLayerObjList = []
    this.sideViewLabelList = []

    this.listenMouseEvents = true
    this.dashedLineScale = 1.25
    this.displayPadding = 5

    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    // Assembly Item data
    // Same format as OrderItem
    this.itemData = {
      stockId: parseInt(itemData.stockId),
      stockPriceGroupId: parseInt(itemData.stockPriceGroupId),
      description: itemData.description || '',
      mark: itemData.mark || '',
      notes: itemData.notes || '',
      quantity: parseInt(itemData.quantity),
      stock: _.cloneDeep(itemData.stock || {}),
      stockPriceGroup: _.cloneDeep(itemData.stockPriceGroup || {}),
      unitPrice: parseFloat(itemData.unitPrice || 0),
      unitPriceOverridden: !!itemData.unitPriceOverridden,
      originalUnitPrice: parseFloat(itemData.originalUnitPrice || 0),
      requiresCustomerApproval: !!itemData.requiresCustomerApproval,
      isExternal: !!itemData.isExternal,
      isCancelled: !!itemData.isCancelled,
      isNCRLocked: !!itemData.isNCRLocked,
      isInvoiced: !!itemData.isInvoiced,
      isDespatched: !!itemData.isDespatched,
      invoicedQuantity: itemData.invoicedQuantity || 0,
      despatchedQuantity: itemData.despatchedQuantity || 0,
      grainDirection: itemData.grainDirection,
      stockFaceSideUpType: itemData.stockFaceSideUpType,
      assemblyOrderItemList: itemData.assemblyOrderItemList || [],
    }
    
    // Precalculated bounds of the group
    this.bounds = new THREE.Box2()
    this.highlightBounds = new THREE.Box2()

    for(var i=0; i<4; i++) {
      let cornerRect = new RectObject(this.editor, {
        width: 4,
        height: 4,
        filled: true,
        colour: this.editor.getTheme().assemblyColour
      })
      this.addChildObject(cornerRect)
      this.rectangleObjList.push(cornerRect)
    }

    this.labelRectObj = new RectObject(this.editor, {
      width: 60,
      height: 16,
      filled: true,
      isCentered: false,
      colour: this.editor.getTheme().assemblyColour
    })
    this.addChildObject(this.labelRectObj)
    this.labelRectLabelObj = new LabelObject(editor, {
      text: 'Assembly',
      colour: '#ffffff',
      anchorX: 'left',
      anchorY: 'top',
      fontSize: 13
    })
    this.addChildObject(this.labelRectLabelObj)

    // Inside / Outside labels
    let outsideLabelObj = new LabelObject(editor, {
      text: 'Outside',
      colour: this.editor.getTheme().dimLabelColour,
      anchorX: 'right',
      anchorY: 'top',
      fontSize: 13
    })
    let insideLabelObj = new LabelObject(editor, {
      text: 'Inside',
      colour: this.editor.getTheme().dimLabelColour,
      anchorX: 'left',
      anchorY: 'top',
      fontSize: 13
    })
    this.addChildObject(outsideLabelObj)
    this.sideViewLabelList.push(outsideLabelObj)
    this.addChildObject(insideLabelObj)
    this.sideViewLabelList.push(insideLabelObj)


    // List of reviewed approval items for the shape
    // Used by the editor review sidebar before finishing the order editor
    this.reviewApprovalList = []
    this.isReviewApproved = false     // Set if all approval items are approved

    // Review Approval Override
    // After opening an order, all panels are automatically approved until modified
    this.reviewApprovalOverride = false
  }

  // Dispose the object
  dispose() {
    super.dispose()

    // Remove stuff
    if(this.outlineGeometry) {
      this.outlineGeometry.dispose()
      this.outlineGeometry = null
    }
    this.outlineObj = null
    if(this.labelRectObj) {
      this.labelRectObj.dispose()
      this.labelRectObj = null
    }
    if(this.labelRectLabelObj) {
      this.labelRectLabelObj.dispose()
      this.labelRectLabelObj = null
    }
    this.sideViewLabelList.forEach(labelObj => {
      labelObj.dispose()
    })
    this.sideviewLayerObjList = []
    this.sideViewLabelList = []
  }


  /**
   * Mouse Move Event
   */
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    // Always show assemblies
    let visible = true

    if(this.outlineObj)
      this.outlineObj.visible = visible
    if(this.labelRectObj)
      this.labelRectObj.visible = visible
    if(this.labelRectLabelObj)
      this.labelRectLabelObj.visible = visible
    this.rectangleObjList.forEach(rectObj => {
      rectObj.setVisible(visible)
    })
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var renderScale = 1.0 / this.editor.camera.zoom

    let colour = this.editor.getTheme().assemblyColour
    if(this.selected)
      colour = this.editor.getTheme().selectionColour

    // Scale the dashes relative to the camera
    if(this.outlineObj) {
      this.outlineObj.material.scale = this.editor.camera.zoom * this.dashedLineScale
      this.outlineObj.material.color.set(colour)
    }
    this.rectangleObjList.forEach(rectObj => {
      rectObj.scale.x = renderScale
      rectObj.scale.y = renderScale
      rectObj.colour = colour
    })
    if(this.labelRectObj) {
      this.labelRectObj.scale.x = renderScale
      this.labelRectObj.scale.y = renderScale
      this.labelRectObj.colour = colour
    }
    if(this.labelRectLabelObj) {
      this.labelRectLabelObj.scale.x = renderScale
      this.labelRectLabelObj.scale.y = renderScale
    }
    this.sideViewLabelList.forEach(labelObj => {
      labelObj.scale.x = renderScale
      labelObj.scale.y = renderScale
    })
  }


  // Build the group object graphics
  updateSceneObject() {
    if(this.bounds.isEmpty())
      return

    this.sideviewLayerObjList.forEach(layerObj => {
      layerObj.dispose()
      this.removeChildObject(layerObj);
    })
    this.sideviewLayerObjList = []

    this.clearSceneObject()

    if(this.outlineGeometry) {
      this.outlineGeometry.dispose()
      this.outlineGeometry = null
    }
    this.outlineObj = null

    // Resolve child items & update bounds based on that
    var sideviewWidth = 500
    var layerPaddingVert = 100
    var layerPositionX = this.highlightBounds.max.x - sideviewWidth;
    
    var displayLayerList = this.generateLayerList()

    displayLayerList.forEach(displayLayerInfo => {
      
      let layerWidth = 50
      let layerHeight = (this.highlightBounds.max.y - this.highlightBounds.min.y) - layerPaddingVert*2

      if(displayLayerInfo.uomType == UOMTypes.length)
        layerHeight = layerWidth

      let itemLayerA = new RectObject(this.editor, {
        width: layerWidth,
        height: layerHeight,
        filled: true,
        isCentered: false,
        opacity: displayLayerInfo.opacity,
        colour: displayLayerInfo.colour
      })
      itemLayerA.userData = {
        editorObjectId: displayLayerInfo.editorObjectId,
        colour: displayLayerInfo.colour,
        opacity: displayLayerInfo.opacity
      }
      itemLayerA.setPosition(new THREE.Vector2(layerPositionX, this.highlightBounds.min.y + layerPaddingVert));
      this.sideviewLayerObjList.push(itemLayerA)
      this.addChildObject(itemLayerA)

      // Display 2nd block for length blocks
      if(displayLayerInfo.uomType == UOMTypes.length) {
        let itemLayerB = new RectObject(this.editor, {
          width: layerWidth,
          height: layerHeight,
          filled: true,
          isCentered: false,
          opacity: displayLayerInfo.opacity,
          colour: displayLayerInfo.colour
        })
        itemLayerB.setPosition(new THREE.Vector2(layerPositionX, this.highlightBounds.max.y - layerPaddingVert - layerHeight));
        this.sideviewLayerObjList.push(itemLayerB)
        this.addChildObject(itemLayerB)
      }

      layerPositionX += 55
    })

    // Sideview Inside / Outside labels
    if(this.sideViewLabelList[0] && this.sideViewLabelList[1]) {
      this.sideViewLabelList[0].setPosition(new THREE.Vector2(this.highlightBounds.max.x - sideviewWidth - 20, this.highlightBounds.min.y + layerPaddingVert))           
      this.sideViewLabelList[1].setPosition(new THREE.Vector2(layerPositionX + 20, this.highlightBounds.min.y + layerPaddingVert))           
    }


    // Circle outline around the bounds of the grouped vertices
    let groupCenter = this.highlightBounds.getCenter(new THREE.Vector2())
    let groupSize = this.highlightBounds.getSize(new THREE.Vector2())

    let groupHalfW = groupSize.x / 2
    let groupHalfH = groupSize.y / 2

    this.rectangleObjList[0].setPosition(new THREE.Vector2(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH))
    this.rectangleObjList[1].setPosition(new THREE.Vector2(groupCenter.x + groupHalfW, groupCenter.y - groupHalfH))
    this.rectangleObjList[2].setPosition(new THREE.Vector2(groupCenter.x + groupHalfW, groupCenter.y + groupHalfH))
    this.rectangleObjList[3].setPosition(new THREE.Vector2(groupCenter.x - groupHalfW, groupCenter.y + groupHalfH))

    if(this.labelRectObj)
      this.labelRectObj.setPosition(new THREE.Vector2(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH))

    if(this.labelRectLabelObj) {
      this.labelRectLabelObj.setPosition(new THREE.Vector2(this.labelRectObj.position.x + 2, this.labelRectObj.position.y + 2))
    }

    var groupShape = new THREE.Shape()
    groupShape.moveTo(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH)
    groupShape.lineTo(groupCenter.x + groupHalfW, groupCenter.y - groupHalfH)
    groupShape.lineTo(groupCenter.x + groupHalfW, groupCenter.y + groupHalfH)
    groupShape.lineTo(groupCenter.x - groupHalfW, groupCenter.y + groupHalfH)
    groupShape.lineTo(groupCenter.x - groupHalfW, groupCenter.y - groupHalfH)
    var vertices = []

    let positionZ = 200
    var shapePoints = groupShape.getPoints(15)
    for(var i = 0; i < shapePoints.length - 1; i++)
      vertices.push(shapePoints[i].x , shapePoints[i].y, positionZ, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, positionZ)

    this.outlineGeometry = new THREE.BufferGeometry(groupShape)
    this.outlineGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))

    var material = new THREE.LineDashedMaterial({
      color: this.editor.getTheme().assemblyColour,
      transparent: false,
      dashSize: 6,
      gapSize: 4,
      scale: this.dashedLineScale
    })

    this.outlineObj = new THREE.LineSegments(this.outlineGeometry, material)
    this.outlineObj.computeLineDistances()
    this.sceneObject.add(this.outlineObj)
  }


  /**
   * Update the layered units for the general items in the assembly
   * Based on shape perimeters
   */
  updateAssemblyItemDetails() {
    // For general items with metre / squareMetre UOM, get neighbouring shape panels
    // Calculate the perimeter / area
    this.itemData.assemblyOrderItemList.forEach(orderItem => {
      if(!orderItem.stockId)
        return

      let levelOrderItems = this.itemData.assemblyOrderItemList.filter(levelItem => levelItem.parentOrderItemId == orderItem.parentOrderItemId)
      let levelOrderItemIndex = levelOrderItems.findIndex(item => item.id == orderItem.id)

      if(orderItem.stockType == InventoryStockTypes.general) {
        // Based on order item stock thickness / dimWidth
        if(orderItem.stock) {
          // If not production completable, ignore it
          if(!orderItem.stock.isProductionCompletable)
            return

          // Based on UoM type
          if(orderItem.stock.uomType == UOMTypes.length || orderItem.stock.uomType == UOMTypes.metresq) {
            
            // Get neighbouring panels of size level in the assembly
            let prevShape = null
            let nextShape = null
            if(levelOrderItemIndex > 0) {
              let prevOrderItem = levelOrderItems[levelOrderItemIndex - 1];
              prevShape = this.findShapeForAssemblyLinkUUID(prevOrderItem.assemblyLinkUUID)
            }
            if(levelOrderItemIndex < levelOrderItems.length - 1) {
              let nextOrderItem = levelOrderItems[levelOrderItemIndex + 1];
              nextShape = this.findShapeForAssemblyLinkUUID(nextOrderItem.assemblyLinkUUID)
            }

            var referenceSegmentList = []
            if(prevShape)
              referenceSegmentList = prevShape.segmentList
            else if(nextShape)
              referenceSegmentList = nextShape.segmentList

            // Handle offsets
            if(orderItem.orderEntryConfigData && orderItem.orderEntryConfigData.assemblyItemData) {
              let edgeOffsetList = orderItem.orderEntryConfigData.assemblyItemData.edgeOffsetList
              if(_.isArray(edgeOffsetList) && edgeOffsetList.length == 4) {
                // Can't really apply 4 offsets to a complex shape that may have more than 4 sides.
                // So we'll grab the largest offset & just do a general inset around the shape & calculate perimeter based on that
                let largestOffset = edgeOffsetList.reduce((curValue, offset) => {
                  return Math.max(curValue, parseFloat(offset || 0))
                }, 0)

                // Inset segment list & calc area / perimeter
                if(Math.abs(largestOffset) > 0.1) {
                  referenceSegmentList = MathsUtils.expandSegmentList(referenceSegmentList, -largestOffset)
                }
              }
            }

            // Calculate length based on perimeters
            if(orderItem.stock.uomType == UOMTypes.length) {
              let shapePerimeter = MathsUtils.calculateSegmentListPerimeter(referenceSegmentList, Config.Settings.arcSubdivisions)

              // Convert to metres
              orderItem.units = Utils.roundNumber(Utils.convertMillimetreToMetre(shapePerimeter), 3)
            }

            // Calculate sequare metres based on area of neighbouring shapes
            if(orderItem.stock.uomType == UOMTypes.metresq) {
              let shapeArea = MathsUtils.calculateSegmentListArea(referenceSegmentList, Config.Settings.arcSubdivisions)

              // Convert to metres^2
              orderItem.units = Utils.roundNumber(Utils.convertMillimetreSqToMetreSq(shapeArea), 3)
            }
          }
        }
      }
    })
  }


  /**
   * Generate the layer list of side view displaying
   */
  generateLayerList() {
    var outputLayerList = []

    this.itemData.assemblyOrderItemList.forEach(orderItem => {
      if(!orderItem.stockId)
        return

      let layerThickness = 0

      if(orderItem.stockType == InventoryStockTypes.glass) {
        //  Find glass shape object & use properties from that
        let glassChildShape = this.findShapeForAssemblyLinkUUID(orderItem.assemblyLinkUUID)

        if(glassChildShape) {
          outputLayerList.push({
            name: glassChildShape.itemData.stock.name,
            stockType: orderItem.stockType,
            uomType: UOMTypes.metresq,
            glassWidth: glassChildShape.calculateWidth(),
            glassHeight: glassChildShape.calculateHeight(),
            thickness: glassChildShape.getThickness(),
            stockFaceSideUpType: glassChildShape.itemData.stockFaceSideUpType,
            colour: glassChildShape.glassColour,
            stock: glassChildShape.itemData.stock,
            opacity: 0.5,
            editorObjectId: glassChildShape.id
          })
        }
      }
      if(orderItem.stockType == InventoryStockTypes.general) {
        // Based on order item stock thickness / dimWidth
        if(orderItem.stock) {
          // If not production completable, ignore it
          if(!orderItem.stock.isProductionCompletable)
            return

          if(orderItem.stock.uomType == UOMTypes.length)
            layerThickness = orderItem.stock.dimWidth

          outputLayerList.push({
            name: orderItem.stock.name,
            stockType: orderItem.stockType,
            uomType: orderItem.stock.uomType,
            thickness: layerThickness,
            colour: '#808080',
            stock: orderItem.stock,
            opacity: 0.5
          })
        }
      }
    })

    return outputLayerList
  }


  /**
   * Child object was updated
   * Resize the assembly if the shape was changed
   */
  childObjectUpdated(childObj) {
    this.resizeBoundsToChildren()
  }


  /**
   * Add a list of glass shapes as children of the assembly group
   * Resizes the bounds to fit
   */
  addChildObjectList(shapeList) {
    shapeList.forEach(shape => {
      this.addChildObject(shape)
    })

    this.resizeBoundsToChildren()
  }


  /**
   * Resize the assembly bounds to fit all the child shapes
   */
  resizeBoundsToChildren() {
    let assemblyPadding = 500
    let assemblySideviewSize = 800

    let childShapes = this.getChildGlassShapeList()
    var assemblyBounds = new THREE.Box2()

    childShapes.forEach(childShape => {
      assemblyBounds.union(childShape.calculateSegmentBounds())
    })

    assemblyBounds.expandByVector(new THREE.Vector2(assemblyPadding, assemblyPadding));
    assemblyBounds.max.x += assemblySideviewSize

    assemblyBounds.expandByScalar(1.2)
    this.bounds = assemblyBounds
    this.highlightBounds = assemblyBounds.clone()

    this.updateSceneObject()
  }


  /**
   * Find a child shape in the assembly given an assembly link UUID
   * Returns null if no shape found
   */
  findShapeForAssemblyLinkUUID(assemblyLinkUUID) {
    return this.children.find(childObj => {
      if(childObj && childObj instanceof GlassShape) {
        return childObj.assemblyLinkUUID == assemblyLinkUUID
      }
      return false
    })
  }

  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    // Check over the top-left rectangle
    if(this.labelRectObj) {
      if(this.labelRectObj.isPointOverRect(ray2DPoint)) {
        closestIntersection = {
          intersection: ray2DPoint,
          position: ray2DPoint,
          objectId: this.id,
          distance: 0
        }

        return closestIntersection
      }
    }

    // Check bounds edges only
    if(ray2DPoint.x >= this.highlightBounds.min.x - radiusTest &&
       ray2DPoint.y >= this.highlightBounds.min.y - radiusTest &&
       ray2DPoint.x <= this.highlightBounds.max.x + radiusTest &&
       ray2DPoint.y <= this.highlightBounds.max.y + radiusTest) {

      var boundsClosestEdgePnt = MathsUtils.calculateClosestBoxEdgePointToPosition2D(this.highlightBounds, ray2DPoint)
      var edgeDistance = boundsClosestEdgePnt.distanceTo(ray2DPoint)

      // If the closest edge point is within the max edge test radius, return it
      if(edgeDistance < radiusTest) {
        closestIntersection = {
          intersection: boundsClosestEdgePnt,
          position: boundsClosestEdgePnt,
          objectId: this.id,
          distance: edgeDistance
        }

        return closestIntersection
      }
    }

    // Check for intersections with the sideview rectangles
    for(let rectObj of this.sideviewLayerObjList) {
      if(rectObj.isPointOverRect(ray2DPoint)) {
        closestIntersection = {
          intersection: ray2DPoint,
          position: ray2DPoint,
          objectId: this.id,
          distance: 0
        }

        return closestIntersection
      }
    }

    return null
  }


  /**
   * Move the assembly group by a 2D offset vector
   * Moves all child shapes by the offset
   */
  moveAssemblyGroup(offset) {
    // Round the offset to nearest mm to avoid issues with items (eg, split lines) not rounded to nearest mm after moving shapes
    offset.x = Math.round(offset.x)
    offset.y = Math.round(offset.y)

    // Move shape will notify the parent assembly of a move & recalculate bounds as required
    this.getChildGlassShapeList().forEach(glassShape => {
      glassShape.moveShape(offset)
    })
  }


  /**
   * Get all children objects that are GlassShape objects
   */
  getChildGlassShapeList() {
    return this.children.filter(childObj => {
      return childObj instanceof GlassShape
    })
  }


  /**
   * A child object in the assembly had their selection status changed
   */
  onChildObjectSelectionUpdated(childObject) {
    // Update layer list statuses
    var objSelected = childObject.getSelected()
    var selectionColour = this.editor.getTheme().selectionColour

    // Update sideview colours based on what is selected
    this.sideviewLayerObjList.forEach(sideViewLayerObj => {
      if(sideViewLayerObj.userData && sideViewLayerObj.userData.editorObjectId) {

        if(childObject.id == sideViewLayerObj.userData.editorObjectId) {

          sideViewLayerObj.colour = objSelected ? selectionColour : sideViewLayerObj.userData.colour
          sideViewLayerObj.opacity = objSelected ? 0.33 : sideViewLayerObj.userData.opacity
        }
      }
    })
  }


  /**
   * Update the item data from an OrderItem based object
   */
  updateOrderItemData(orderItem, orderItemList) {
    this.itemData.orderItemId = orderItem.id
    this.itemData.stockId = parseInt(orderItem.stockId)
    this.itemData.stockPriceGroupId = parseInt(orderItem.stockPriceGroupId || 0)
    this.itemData.glassLevelId = parseInt(orderItem.glassLevelId || 0)

    this.itemData.description = orderItem.description || ''
    this.itemData.mark = orderItem.mark || ''
    this.itemData.notes = orderItem.notes || ''

    this.itemData.quantity = parseInt(orderItem.quantity)
    this.itemData.unitPrice = parseFloat(orderItem.unitPrice || 0)
    this.itemData.unitPriceOverridden = !!orderItem.unitPriceOverridden
    this.itemData.originalUnitPrice = parseFloat(orderItem.originalUnitPrice || 0)

    this.itemData.stock = _.cloneDeep(orderItem.stock || {})
    this.itemData.stockPriceGroup = _.cloneDeep(orderItem.stockPriceGroup || {})
    this.itemData.glassLevel = _.cloneDeep(orderItem.glassLevel || {})

    this.itemData.requiresCustomerApproval = !!orderItem.requiresCustomerApproval
    this.itemData.isExternal = !!orderItem.isExternal
    this.itemData.isCancelled = !!orderItem.isCancelled
    this.itemData.isNCRLocked = !!orderItem.isNCRLocked
    this.itemData.isInvoiced = !!orderItem.isInvoiced
    this.itemData.isDespatched = !!orderItem.isDespatched
    this.itemData.invoicedQuantity = parseInt(orderItem.invoicedQuantity || 0)
    this.itemData.despatchedQuantity = parseInt(orderItem.despatchedQuantity || 0)

    this.itemData.discountRate = parseFloat(orderItem.discountRate || 0)
    this.itemData.discountAmount = parseFloat(orderItem.discountAmount || 0)

    this.itemData.grainDirection = orderItem.grainDirection
    this.itemData.stockFaceSideUpType = orderItem.stockFaceSideUpType

    // Go through children & update from order items in order item list
    if(this.children && _.isArray(orderItemList)) {
      this.children.forEach(childObject => {
        if(childObject instanceof GlassShape || childObject instanceof AssemblyGroup) {

          // OrderItem id's can change during new orders when switch between editor & item list
          // Resolve the links via BaseObject id in OrderItem.editorData.data
          let childOrderItem = orderItemList.find(listItem => {
            if(_.isObject(listItem.editorData) && _.isObject(listItem.editorData.data)) {
              return listItem.editorData.data.id == childObject.id
            }
            return false
          })
          if(childOrderItem)
            childObject.updateOrderItemData(childOrderItem, orderItemList)
        }
      })
    }   

    // Update any other assembly internal order items from the main order item list
    // Done if any order items were edited on the line-item version instead of the editor
    // May not be required... Normal glass items don't read back in order items manually edited by line-items...
    let childOrderItems = orderItemList.filter(item => item.parentOrderItemId == orderItem.id && _.isObject(item.editorData))
    this.itemData.assemblyOrderItemList.forEach(storedOrderItem => {

      let listItem = childOrderItems.find(item => item.editorData.assemblyLinkUUID == storedOrderItem.assemblyLinkUUID)
      if(listItem) {

        // Update order item ID links
        storedOrderItem.orderItemId = listItem.id

        // Only handle general items
        // Glass / Assembly / services get handled seperately
        if(storedOrderItem.stockType != InventoryStockTypes.general)
          return
        
        // Copy over any editable items from the frontend OrderItemList
        storedOrderItem.stockId = parseInt(listItem.stockId)
        storedOrderItem.description = listItem.description || ''
        storedOrderItem.mark = listItem.mark || ''
        storedOrderItem.notes = listItem.notes || ''

        storedOrderItem.unitPrice = parseFloat(listItem.unitPrice || 0)
        storedOrderItem.unitPriceOverridden = !!listItem.unitPriceOverridden
        storedOrderItem.originalUnitPrice = parseFloat(listItem.originalUnitPrice || 0)

        storedOrderItem.stock = _.cloneDeep(listItem.stock || {})
      }
    })
  }


  /**
   * Check if the assembly is from a cancelled order item
   */
  isCancelled() {
    return !!this.itemData.isCancelled
  }


  /**
   * Check if the assembly is from an NCR locked order item
   */
  isNCRLocked() {
    return !!this.itemData.isNCRLocked
  }


  /**
   * Check if the order item linked to the assembly has been invoiced has all
   */
  isInvoiced() {
    return !!this.itemData.isInvoiced || (this.itemData.invoicedQuantity || 0) > 0
  }


  /**
   * Check if the order item linked to the assembly has been despatched at all
   */
  isDespatched() {
    return !!this.itemData.isDespatched || (this.itemData.despatchedQuantity || 0) > 0
  }


  /**
   * Clone the assembly object
   */
  clone() {
    let newGroup = new AssemblyGroup(this.editor)

    newGroup.bounds = this.bounds.clone()
    newGroup.highlightBounds = this.highlightBounds.clone()

    newGroup.updateSceneObject()

    return newGroup
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.bounds = MathsUtils.serializeBox2(this.bounds)
    outputData.highlightBounds = MathsUtils.serializeBox2(this.highlightBounds)
    outputData.assemblyLinkUUID = this.assemblyLinkUUID

    // Stock & order item data
    // Trim out some data from DB objects so we only store absolute minimum & free up database
    outputData.itemData = _.cloneDeep(this.itemData)
    outputData.itemData.stock = _.cloneDeep(_.pick(this.itemData.stock || {}, [
      'id', 'name', 'code', 'thickness', 'glassSalesPriceList', 'glassType', 'isStockUsingFaces', 'isStockUsingGrain', 'stockMaterialType', 'stockColour1', 'stockColour2', 'stockColourOpacity', 'isProductionCompletable', 'dimWidth', 'dimHeight', 'uomType', 'profileType'
    ]))
    outputData.itemData.stockPriceGroup =  _.cloneDeep(_.pick(this.itemData.stockPriceGroup || {}, [
      'id', 'name', 'isActive', 'isStockLinked'
    ]))

    // Cleanup itemData.assemblyOrderItemList so it only stores absolute minimum of data
    if(outputData.itemData.assemblyOrderItemList) {
      outputData.itemData.assemblyOrderItemList.forEach(assemblyOrderItem => {

        assemblyOrderItem.stock = _.cloneDeep(_.pick(assemblyOrderItem.stock || {}, [
          'id', 'name', 'code', 'thickness', 'glassSalesPriceList', 'glassType', 'isStockUsingFaces', 'isStockUsingGrain', 'stockMaterialType', 'stockColour1', 'stockColour2', 'stockColourOpacity', 'isProductionCompletable', 'dimWidth', 'dimHeight', 'uomType', 'profileType'
        ]))
        assemblyOrderItem.stockPriceGroup =  _.cloneDeep(_.pick(assemblyOrderItem.stockPriceGroup || {}, [
          'id', 'name', 'isActive', 'isStockLinked'
        ]))
      })
    }

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.bounds = MathsUtils.deserializeBox2(object.bounds)
    this.highlightBounds = MathsUtils.deserializeBox2(object.highlightBounds)
    this.assemblyLinkUUID = object.assemblyLinkUUID || ''

    // Stock & order item data
    this.itemData = _.cloneDeep(object.itemData)

    this.updateSceneObject()
  }

  /**
   * Calculate the largest size for the panels in the assembly
   * 
   * @returns {Object} Returns an object with width / height properties
   */
  calculateLargestSize() {
    var size = {
      width: 0,
      height: 0
    }

    this.getChildGlassShapeList().forEach(glassShape => {
      var glassCutBounds = glassShape.calculateGlassCutBounds()
      var panelWidth = Math.round(glassCutBounds.max.x - glassCutBounds.min.x)
      var panelHeight = Math.round(glassCutBounds.max.y - glassCutBounds.min.y)

      size.width = Math.max(size.width, panelWidth)
      size.height = Math.max(size.height, panelHeight)
    })

    return size
  }


  /**
   * Reset the review approval list for the assembly
   * Done when the assembly has been modified in a way that requires re-approval by the review sidebar
   */
  resetReviewApprovals() {
    this.reviewApprovalList = []
    this.isReviewApproved = false
    this.reviewApprovalOverride = false

    this.editor.flagRenderRequired()
  }


  /**
   * Flag the assembly review approvals in the review process
   */
  updateReviewApprovals(approvalList) {
    // Turn off override when updating from review
    this.reviewApprovalOverride = false

    this.reviewApprovalList = approvalList
    this.isReviewApproved = this.reviewApprovalList.every(approval => approval.approved)

    this.updateSceneObject()
    this.editor.flagRenderRequired()
  }


  /**
   * Get the flag if the assembly is fully approved in review
   */
  isAssemblyReviewApproved() {
    return this.isReviewApproved || this.reviewApprovalOverride
  }
}